import type { Dispatch, SetStateAction } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Button, FormGroup, Text } from '@ksyos/design-system';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { IndependentDateInput } from '@frontend/components/fields';
import { translateKey } from '@frontend/translate';

interface IOutclinicDashboardSnoozeModalProps {
    isOpen: boolean;
    processId: string;
    processNumber?: number;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    setSnoozeStatus: (processId: string, isSnoozed: boolean, snoozedUntilDateTime?: string) => void;
    snoozedUntilDateTime?: string;
}

export function OutclinicDashboardSnoozeModal(props: IOutclinicDashboardSnoozeModalProps) {
    const initialDate = props.snoozedUntilDateTime || null;
    const [date, setDate] = useState<string | null>(initialDate);
    const [isRemoving, setIsRemoving] = useState(false);

    const actionButtons = [
        <Button
            key={uuidv4()}
            onClick={handleClose}
            variant="outline"
        >
            {translateKey('common.cancelling')}
        </Button>,
    ];

    // Remove button
    if (props.snoozedUntilDateTime) {
        const key = isRemoving ? 'common.confirmRemove' : 'outclinicDashboard.removeSnooze';
        actionButtons.push(
            <Button
                key={uuidv4()}
                onClick={handleRemove}
                variant={isRemoving ? 'solid' : 'outline'}
            >
                {translateKey(key)}
            </Button>
        );
    }
    // Edit/save button
    if (!isRemoving) {
        actionButtons.push(
            <Button
                isDisabled={!date || date === initialDate}
                key={uuidv4()}
                onClick={handleUpdate}
            >
                {props.snoozedUntilDateTime
                    ? translateKey('outclinicDashboard.editSnooze')
                    : translateKey('common.save')}
            </Button>
        );
    }

    return (
        <Modal
            actionButtons={actionButtons}
            isOpen={props.isOpen}
            onClose={handleClose}
            size="medium"
            title={translateKey(computeTitle(), `${props.processNumber}`)}
        >
            {isRemoving ? (
                <Text>{translateKey('outclinicDashboard.modalConfirmRemove')}</Text>
            ) : (
                <FormGroup
                    isRequired={true}
                    label={translateKey('outclinicDashboard.modalDatepickerLabel')}
                >
                    <IndependentDateInput
                        hasMaxWidth={true}
                        maxDate={DateTime.now().add(3, 'months').format()}
                        minDate={DateTime.now().add(1, 'day').format()}
                        onChange={(newDate) => {
                            setDate(newDate);
                        }}
                        placeholder={translateKey('common.dateFormat')}
                        size="large"
                        value={date}
                        variant="outline"
                    />
                </FormGroup>
            )}
        </Modal>
    );

    function computeTitle(): string {
        if (isRemoving) {
            return 'outclinicDashboard.modalTitleRemove';
        }

        return props.snoozedUntilDateTime ? 'outclinicDashboard.modalTitleEdit' : 'outclinicDashboard.modalTitle';
    }

    function handleClose(): void {
        setDate(initialDate);
        setIsRemoving(false);
        props.setIsOpen(false);
    }

    function handleRemove(): void {
        if (isRemoving) {
            props.setSnoozeStatus(props.processId, false);
        } else {
            setIsRemoving(true);
        }
    }

    function handleUpdate(): void {
        if (date) {
            props.setSnoozeStatus(props.processId, true, date);
        }
        props.setIsOpen(false);
    }
}
